/*
 * NEXZ V2 — Complete the Set (Stage F, extended post-Gate-K)
 *
 * Adds the current product's selected variant plus a chosen variant of
 * the linked partner product in a single request. Horizon's own
 * product-form.js only ever submits its own single-variant form, so it
 * has no path for a two-line add — that's the one documented gap this
 * file exists to cover (per the Stage F approval condition: no forks
 * unless a documented gap requires a minimal adapter).
 *
 * Post-Gate-K correction: the original version submitted the partner's
 * default variant with no choice at all. This version reads the color
 * and size the shopper picked from the server-rendered chip markup in
 * blocks/nexz-complete-set.liquid — every (color, size) combination's
 * price display is already computed and formatted by Liquid at render
 * time (data-combined-display / data-final-display), so this file does
 * no currency math or formatting of its own; it only reads and swaps
 * text that Liquid already produced.
 *
 * Everything cart-facing is reused, not reinvented: Theme.routes.cart_add_url
 * (the same global the rest of the theme uses, defined in
 * snippets/scripts.liquid) and fetchConfig from @theme/utilities (the
 * same helper product-form.js itself uses to build the request). The
 * dispatched event is a real CartLinesUpdateEvent from @shopify/events,
 * so the cart drawer, header cart count, and this file's own analytics
 * listener in nexz-pdp.js all observe the same event shape a native add
 * would produce — this is not a second, forked cart-update signal.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

class NexzCompleteSet extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('[data-nexz-set-add]');
    this.#combinedEl = this.querySelector('[data-nexz-set-combined]');
    this.#finalEl = this.querySelector('[data-nexz-set-final]');
    this.#errorEl = this.querySelector('[data-nexz-set-error]');
    this.#labelEl = this.querySelector('[data-nexz-set-add-label]');
    this.#partnerLink = this.querySelector('[data-nexz-set-partner-link]');
    this.#partnerName = this.querySelector('[data-nexz-set-partner-name]');

    if (!this.#button) return;

    this.querySelectorAll('[data-nexz-set-color]').forEach((radio) => {
      radio.addEventListener('change', () => this.#handleColorChange(radio));
    });

    this.querySelectorAll('[data-nexz-set-size]').forEach((radio) => {
      radio.addEventListener('change', () => this.#handleSizeChange(radio));
    });

    this.#button.addEventListener('click', () => this.#handleAdd());
  }

  /** @param {HTMLInputElement} radio */
  #handleColorChange(radio) {
    const colorId = radio.value;

    this.querySelectorAll('[data-nexz-set-size-group]').forEach((group) => {
      group.hidden = group.dataset.colorId !== colorId;
    });

    // Switching color invalidates any size chosen under the previous
    // color — re-lock the gate rather than silently keeping a variant
    // id from a group that's no longer visible.
    this.querySelectorAll('[data-nexz-set-size]').forEach((sizeRadio) => {
      sizeRadio.checked = false;
    });
    this.#setGated(true);

    if (this.#partnerLink) this.#partnerLink.href = radio.dataset.partnerUrl || this.#partnerLink.href;
    if (this.#partnerName) {
      this.#partnerName.href = radio.dataset.partnerUrl || this.#partnerName.href;
      if (radio.dataset.partnerTitle) this.#partnerName.textContent = radio.dataset.partnerTitle;
    }
    if (this.#partnerLink && radio.dataset.partnerImageSrc) {
      const img = this.#partnerLink.querySelector('img');
      if (img) {
        img.src = radio.dataset.partnerImageSrc;
        if (radio.dataset.partnerTitle) img.alt = radio.dataset.partnerTitle;
      }
    }
  }

  /** @param {HTMLInputElement} radio */
  #handleSizeChange(radio) {
    if (this.#combinedEl && radio.dataset.combinedDisplay) this.#combinedEl.textContent = radio.dataset.combinedDisplay;
    if (this.#finalEl && radio.dataset.finalDisplay) this.#finalEl.textContent = radio.dataset.finalDisplay;
    this.#setGated(false);
  }

  /** @param {boolean} gated */
  #setGated(gated) {
    if (!this.#button) return;
    if (gated) {
      this.#button.setAttribute('aria-disabled', 'true');
      if (this.#labelEl) this.#labelEl.textContent = this.#button.dataset.selectSizeLabel || this.#labelEl.textContent;
    } else {
      this.#button.removeAttribute('aria-disabled');
      if (this.#labelEl) this.#labelEl.textContent = this.#button.dataset.addLabel || this.#labelEl.textContent;
    }
  }

  #handleAdd() {
    if (!this.#button || this.#button.getAttribute('aria-disabled') === 'true') return;

    const currentVariantId = this.dataset.currentVariantId;
    const sizeRadio = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-nexz-set-size]:checked'));
    const partnerVariantId = sizeRadio ? sizeRadio.value : null;
    // The actually-added colorway, not necessarily the originally-linked
    // set_partner — the shopper may have switched color before adding.
    // Falls back to the static default for the no-color-picker case
    // (partner has no sibling colorways, so no [data-nexz-set-color] exists).
    const colorRadio = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-nexz-set-color]:checked'));
    const partnerProductId = colorRadio ? colorRadio.value : this.dataset.partnerProductId;
    if (!currentVariantId || !partnerVariantId) return;

    this.#button.setAttribute('aria-disabled', 'true');
    if (this.#errorEl) this.#errorEl.hidden = true;

    const lines = [
      { merchandiseId: `gid://shopify/ProductVariant/${currentVariantId}`, quantity: 1 },
      { merchandiseId: `gid://shopify/ProductVariant/${partnerVariantId}`, quantity: 1 },
    ];

    let resolvePromise, rejectPromise;
    const deferredPromise = new Promise((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });
    // Handled here as well as by cart-drawer.js — see nexz-cart-undo.js for
    // the rationale. The inline error element below stays the user-facing
    // report; this only stops an unhandled rejection reaching monitoring.
    deferredPromise.catch(() => {});

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'set',
        lines,
        promise: deferredPromise,
      })
    );

    const fetchCfg = fetchConfig('json', {
      body: JSON.stringify({
        items: [
          { id: Number(currentVariantId), quantity: 1 },
          { id: Number(partnerVariantId), quantity: 1 },
        ],
      }),
    });

    fetch(Theme.routes.cart_add_url, fetchCfg)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const message = response.description || response.message || 'Could not add the set to cart.';
          rejectPromise(new Error(message));
          if (this.#errorEl) {
            this.#errorEl.hidden = false;
            this.#errorEl.textContent = message;
          }
          return;
        }
        resolvePromise({ cart: response, detail: { didError: false } });
        this.dispatchEvent(
          new CustomEvent('nexz:set_add', {
            bubbles: true,
            detail: { productId: partnerProductId, currentVariantId, partnerVariantId },
          })
        );
        document.dispatchEvent(
          new CustomEvent('nexz:set_add', {
            bubbles: true,
            detail: { productId: partnerProductId, currentVariantId, partnerVariantId },
          })
        );
        window.location.href = Theme.routes.cart_url;
      })
      .catch((error) => {
        rejectPromise(error);
        if (this.#errorEl) {
          this.#errorEl.hidden = false;
          this.#errorEl.textContent = 'Could not add the set to cart.';
        }
      })
      .finally(() => {
        this.#button.removeAttribute('aria-disabled');
      });
  }

  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {HTMLElement | null} */
  #combinedEl = null;
  /** @type {HTMLElement | null} */
  #finalEl = null;
  /** @type {HTMLElement | null} */
  #errorEl = null;
  /** @type {HTMLElement | null} */
  #labelEl = null;
  /** @type {HTMLAnchorElement | null} */
  #partnerLink = null;
  /** @type {HTMLAnchorElement | null} */
  #partnerName = null;
}

if (!customElements.get('nexz-complete-set')) {
  customElements.define('nexz-complete-set', NexzCompleteSet);
}
